/**
 * WelcomePage — LoanKnow public-facing landing.
 *
 * Story arc:
 *   1. Hero ............... value prop in 6 words + immediate CTA
 *   2. Problem ............ "the way mortgages work today is backwards"
 *   3. The new way ........ four steps, visualized
 *   4. Live preview ....... a working score dial + verdict card so the visitor
 *                           sees what they'll actually get
 *   5. Meet Owen .......... AI guide section, humble framing
 *   6. Side-by-side ....... traditional vs LoanKnow comparison table
 *   7. Honest numbers ..... real Supabase counters (no fabricated metrics)
 *   8. FAQ ................ kills the top 6 objections
 *   9. Final CTA .......... low-friction primary button
 *  10. ComplianceFooter
 */
import { useState, useEffect } from 'react';
import {
  CheckCircle, ArrowRight, Shield, ClipboardList, Sparkles, Send,
  FileCheck, ChevronDown, ChevronUp, Lock, Eye,
  AlertTriangle, X, FileText, Award, Zap, Lightbulb,
} from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { LK_FEATURES } from '../config/features';
import BorrowerHeader from '../components/layout/BorrowerHeader';
import ComplianceFooter from '../components/common/ComplianceFooter';
import testimonialsData from '../data/testimonials.json';
import { useEarlyAccess } from '../components/common/EarlyAccessProvider';

// Brand palette — aligned with the Owen readiness experience
// (warm off-whites + the lk indigo system; see src/styles/readiness.css).
const COLORS = {
  ink: '#18181b',
  ink2: '#3f3f46',
  ink3: '#71717a',
  ink4: '#a1a1aa',
  rule: '#eceae5',
  ruleSoft: '#f0eee9',
  bg: '#faf9f7',
  brand: '#4f46e5',
  brandSoft: '#eef2ff',
  brandBorder: '#e0e7ff',
  brandDark: '#312e81',
  brandBody: '#3730a3',
  good: '#059669',
  goodSoft: '#ecfdf5',
  goodDeep: '#047857',
  warn: '#d97706',
  warnSoft: '#fffbeb',
  bad: '#e11d48',
  badSoft: '#fff1f2',
};

const OWEN_AVATAR = '/assets/owen-avatar.png';

// ─────────────────────────────────────────────────────────────
// Hero — calm, warm, with a live preview of the Owen chat
// ─────────────────────────────────────────────────────────────
const HERO_CHIPS = ['Buying a home', 'Refinancing'];

const Hero = ({ startApplication }) => (
  <section style={{
    position: 'relative',
    backgroundColor: COLORS.bg,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  }}>
    {/* Soft indigo glow accents */}
    <div
      aria-hidden
      style={{
        position: 'absolute', top: -180, right: -140,
        width: 460, height: 460, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 70%)',
      }}
    />
    <div
      aria-hidden
      style={{
        position: 'absolute', bottom: -200, left: -160,
        width: 420, height: 420, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(79,70,229,0.06) 0%, transparent 70%)',
      }}
    />

    {/* Sign in opens the passwordless email-code popup on /readiness so
        returning borrowers pick up right where they left off. */}
    <BorrowerHeader signInPath="/readiness?signin=1" />

    <div style={{
      position: 'relative', zIndex: 1,
      maxWidth: 1140, width: '100%', margin: '0 auto',
      padding: '72px 24px 88px',
      display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)',
      gap: 56, alignItems: 'center',
    }}
    className="lk-hero-grid"
    >
      {/* Left: message */}
      <div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 14px', borderRadius: 999,
          backgroundColor: COLORS.brandSoft,
          border: `1px solid ${COLORS.brandBorder}`,
          fontSize: 13, fontWeight: 500, color: COLORS.brandDark,
          marginBottom: 26,
        }}>
          <Sparkles size={14} color={COLORS.brand} /> Free · No credit pull · No sales calls
        </div>

        <h1 style={{
          fontSize: 'clamp(2.4rem, 5vw, 3.9rem)',
          fontWeight: 800, lineHeight: 1.06,
          letterSpacing: '-0.025em', color: COLORS.ink,
          margin: 0, marginBottom: 22,
        }}>
          Know your mortgage.<br />
          <span style={{ color: COLORS.brand }}>Before you talk to anyone.</span>
        </h1>

        <p style={{
          fontSize: 'clamp(1rem, 1.6vw, 1.15rem)',
          lineHeight: 1.6, color: COLORS.ink2,
          maxWidth: 520, margin: '0 0 34px',
        }}>
          Owen, our AI guide, asks about a dozen plain-English questions. Your Readiness Score
          builds live as you answer, you see exactly which loan programs fit you — and when
          you're ready, vetted lenders compete for your file without ever seeing your name.
        </p>

        <div style={{
          display: 'flex', gap: 14, alignItems: 'center',
          flexWrap: 'wrap', marginBottom: 30,
        }}>
          <button
            onClick={() => startApplication('hero_primary')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              backgroundColor: COLORS.brand, color: 'white',
              padding: '16px 32px', fontSize: 16, fontWeight: 600,
              borderRadius: 12, border: 'none', cursor: 'pointer',
              boxShadow: '0 8px 24px -8px rgba(79,70,229,0.45)',
              transition: 'transform 0.15s ease, background 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#4338ca'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = COLORS.brand; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Get My Readiness Score <ArrowRight size={18} />
          </button>
          <button
            onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              backgroundColor: 'white', color: COLORS.ink,
              padding: '16px 26px', fontSize: 15, fontWeight: 600,
              borderRadius: 12, border: '1px solid #dedbd4',
              cursor: 'pointer',
            }}
          >
            See how it works
          </button>
        </div>

        <div style={{
          display: 'flex', gap: 24, flexWrap: 'wrap',
          color: COLORS.ink3, fontSize: 13,
        }}>
          <TrustItem>About 3 minutes</TrustItem>
          <TrustItem>No credit check to start</TrustItem>
          <TrustItem>Nothing shared until you say so</TrustItem>
        </div>
      </div>

      {/* Right: live chat preview */}
      <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        {/* Owen bubble */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <img src={OWEN_AVATAR} alt="" style={{ width: 34, height: 34, flex: 'none', objectFit: 'contain' }} />
          <div style={{
            background: '#fff', border: `1px solid ${COLORS.rule}`,
            borderRadius: '16px 16px 16px 4px', padding: '12px 16px',
            fontSize: 14.5, lineHeight: 1.55, color: COLORS.ink2,
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
          }}>
            Hi, I'm Owen 👋 I'm here to help you figure out how mortgage-ready you are — no
            credit pull, no sales calls, nothing shared with anyone.
          </div>
        </div>

        {/* User answer bubble */}
        <div style={{
          alignSelf: 'flex-end', background: COLORS.brand, color: '#fff',
          borderRadius: '16px 16px 4px 16px', padding: '11px 16px',
          fontSize: 14.5, fontWeight: 500,
        }}>
          Buying a home
        </div>

        {/* Owen's take */}
        <div style={{
          marginLeft: 42, background: COLORS.brandSoft,
          border: `1px solid ${COLORS.brandBorder}`, borderRadius: 12,
          padding: '11px 15px', alignSelf: 'flex-start',
        }}>
          <div style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: COLORS.brand, marginBottom: 3,
          }}>
            Owen's take
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: COLORS.brandBody }}>
            20%+ down — that eliminates mortgage insurance entirely and unlocks the lowest
            rates. Great planning.
          </div>
        </div>

        {/* Composer preview */}
        <div style={{
          background: '#fff', border: '1px solid #e7e4de', borderRadius: 16,
          padding: 16, boxShadow: '0 4px 12px -4px rgba(24,24,27,0.08)',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {HERO_CHIPS.map((c) => (
              <span key={c} style={{
                fontSize: 14, fontWeight: 500, color: COLORS.ink2,
                background: COLORS.bg, border: '1px solid #dedbd4',
                borderRadius: 999, padding: '10px 16px',
              }}>
                {c}
              </span>
            ))}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 14,
            borderTop: '1px dashed #e7e4de', paddingTop: 12,
            fontSize: 13.5, color: '#b3aea6',
          }}>
            <span style={{ flex: 1 }}>Or ask Owen anything — "What's PMI?"</span>
            <span style={{
              fontSize: 12.5, fontWeight: 600, color: COLORS.brand,
              background: COLORS.brandSoft, border: `1px solid ${COLORS.brandBorder}`,
              borderRadius: 999, padding: '7px 16px',
            }}>
              Ask
            </span>
          </div>
        </div>

        {/* Score strip */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          background: '#fff', border: `1px solid ${COLORS.rule}`,
          borderRadius: 14, padding: '12px 16px',
        }}>
          <div style={{ position: 'relative', width: 46, height: 46, flex: 'none' }}>
            <svg width="46" height="46" viewBox="0 0 46 46">
              <circle cx="23" cy="23" r="19" fill="none" stroke={COLORS.rule} strokeWidth="5" />
              <circle
                cx="23" cy="23" r="19" fill="none" stroke={COLORS.brand} strokeWidth="5"
                strokeLinecap="round" strokeDasharray="119.4" strokeDashoffset={119.4 * (1 - 0.64)}
                transform="rotate(-90 23 23)"
              />
            </svg>
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 800, color: COLORS.ink,
            }}>
              64
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.brand }}>Good</div>
            <div style={{ fontSize: 12, color: COLORS.ink3, lineHeight: 1.45 }}>
              Your Readiness Score builds in real time as you answer.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TrustItem = ({ children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <CheckCircle size={14} /> {children}
  </span>
);

// ─────────────────────────────────────────────────────────────
// The Problem — gut-check the visitor
// ─────────────────────────────────────────────────────────────
const ProblemSection = () => {
  const pains = [
    {
      icon: <AlertTriangle size={22} />,
      title: 'You walk in blind',
      body: "You call a lender, they pull your credit on day one, and the conversation starts with their pitch — not your needs.",
    },
    {
      icon: <Eye size={22} />,
      title: 'You can\'t compare honestly',
      body: 'You don\'t know which programs you actually qualify for, what the trade-offs are, or whether the rate you\'re hearing is fair.',
    },
    {
      icon: <Lock size={22} />,
      title: 'The lender owns the file',
      body: "Once you start, your application lives inside one lender's system. Switching means starting over.",
    },
    {
      icon: <X size={22} />,
      title: 'Surprises late in the process',
      body: 'A condition you didn\'t know about shows up two weeks before close. Now you\'re scrambling.',
    },
  ];

  return (
    <section style={{ padding: '96px 24px', backgroundColor: '#fff' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionEyebrow>The way it works today is backwards</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, marginBottom: 18, color: COLORS.ink,
          textAlign: 'center', maxWidth: 720, margin: '8px auto 18px',
        }}>
          You're expected to commit before you understand the deal.
        </h2>
        <p style={{
          fontSize: 17, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 640, margin: '0 auto 56px',
        }}>
          Most mortgage shoppers hand over the most important purchase of their life
          to a salesperson before knowing what they're getting into.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 20,
        }}>
          {pains.map((p) => (
            <div
              key={p.title}
              style={{
                padding: 24, borderRadius: 12,
                backgroundColor: COLORS.bg,
                border: `1px solid ${COLORS.rule}`,
              }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                backgroundColor: COLORS.badSoft, color: COLORS.bad,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14,
              }}>
                {p.icon}
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: COLORS.ink }}>
                {p.title}
              </h3>
              <p style={{ fontSize: 14, color: COLORS.ink2, lineHeight: 1.55, margin: 0 }}>
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// The New Way — 4 steps, illustrated
// ─────────────────────────────────────────────────────────────
const HowItWorks = ({ startApplication }) => {
  const steps = [
    {
      num: '01',
      icon: <ClipboardList size={26} />,
      title: 'Chat with Owen',
      desc: 'About a dozen plain-English questions — tap an answer or type a number. Roughly 3 minutes. No credit pull.',
      detail: 'Ask Owen anything along the way — "What\'s PMI?", "How does escrow work?" — and he explains in plain English before picking back up where you left off.',
    },
    {
      num: '02',
      icon: <Sparkles size={26} />,
      title: 'See your score and your programs',
      desc: 'Your Readiness Score builds live as you answer. At the end, Owen shows the loan programs that actually fit you.',
      detail: 'What you share earns 60% of each category. Uploading proof, then our verification that it matches, unlocks the rest — so lenders trust your file.',
    },
    {
      num: '03',
      icon: <FileCheck size={26} />,
      title: 'Build your application, your way',
      desc: 'A few fields at a time, scoped strictly to your program — Owen never asks for anything it doesn\'t need.',
      detail: 'Every field is optional and everything saves as you go. Your email is your whole account — no password, ever. We send a secure code when you return.',
    },
    {
      num: '04',
      icon: <Send size={26} />,
      title: 'Let lenders compete for you',
      desc: 'Your file goes to vetted lenders anonymously. They see your Readiness Score — never your name — and compete on rate.',
      detail: 'You review real offers side by side and reveal your identity only when you accept. LoanKnow is completely free for you \u2014 lenders pay to compete for prepared borrowers, never the other way around.',
    },
  ];

  return (
    <section
      id="how-it-works"
      style={{ padding: '96px 24px', backgroundColor: COLORS.bg }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionEyebrow color={COLORS.brand}>The LoanKnow way</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, marginBottom: 18, color: COLORS.ink,
          textAlign: 'center', maxWidth: 720, margin: '8px auto 18px',
        }}>
          Get prepared first. Then choose your lender.
        </h2>
        <p style={{
          fontSize: 17, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 640, margin: '0 auto 56px',
        }}>
          Four simple steps that turn the mortgage process inside-out — you arrive
          at the lender's door already knowing where you stand.
        </p>

        <div style={{ display: 'grid', gap: 20 }}>
          {steps.map((s) => (
            <div
              key={s.num}
              className="lk-step-row"
              style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 1.4fr',
                gap: 24, alignItems: 'flex-start',
                padding: 28, borderRadius: 14,
                backgroundColor: '#fff',
                border: `1px solid ${COLORS.rule}`,
              }}
            >
              <div style={{
                fontSize: 38, fontWeight: 800, lineHeight: 1,
                color: COLORS.brand, fontVariantNumeric: 'tabular-nums',
              }}>
                {s.num}
              </div>
              <div>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  backgroundColor: COLORS.brandSoft, color: COLORS.brand,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 14,
                }}>
                  {s.icon}
                </div>
                <h3 style={{
                  fontSize: 18, fontWeight: 700, color: COLORS.ink,
                  marginBottom: 6,
                }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: 14, color: COLORS.ink2, lineHeight: 1.55, margin: 0 }}>
                  {s.desc}
                </p>
              </div>
              <div style={{
                paddingLeft: 24, borderLeft: `1px solid ${COLORS.rule}`,
                fontSize: 14, color: COLORS.ink3, lineHeight: 1.65,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8, color: COLORS.ink4 }}>
                  What this gets you
                </div>
                {s.detail}
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <button
            onClick={() => startApplication('how_it_works_cta')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              backgroundColor: COLORS.brand, color: 'white',
              padding: '15px 32px', fontSize: 15, fontWeight: 600,
              borderRadius: 12, border: 'none', cursor: 'pointer',
              boxShadow: '0 8px 24px -8px rgba(79,70,229,0.45)',
            }}
          >
            Start step one — it's free <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// What you get — live preview of the deliverables
// ─────────────────────────────────────────────────────────────
const WhatYouGet = () => {
  const sampleScore = 72;
  const ringC = 2 * Math.PI * 58;
  const tierCats = [
    { label: 'Income readiness', color: '#4f46e5', pts: '12/25', pct: 48, proof: 20, verif: 12 },
    { label: 'Assets & cash', color: '#059669', pts: '10/20', pct: 50, proof: 21, verif: 13 },
    { label: 'Credit readiness', color: '#7c3aed', pts: '10/20', pct: 50, proof: 21, verif: 13 },
    { label: 'Loan structure fit', color: '#d97706', pts: '9/15', pct: 60, proof: 25, verif: 15 },
  ];
  return (
    <section style={{ padding: '96px 24px', backgroundColor: '#fff' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionEyebrow>What you actually get</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, marginBottom: 18, color: COLORS.ink,
          textAlign: 'center', maxWidth: 720, margin: '8px auto 18px',
        }}>
          A score you can grow, programs that fit, offers that compete.
        </h2>
        <p style={{
          fontSize: 17, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 640, margin: '0 auto 56px',
        }}>
          Not a marketing gimmick. A real readiness picture built from your answers — with a
          clear path to raise it, the loan programs that match your profile, and vetted lenders
          bidding for your anonymous file.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 24,
        }}>
          {/* Readiness Score preview */}
          <PreviewCard label="Your Readiness Score" icon={<Award size={16} />}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, paddingTop: 6 }}>
              <div style={{ position: 'relative', width: 130, height: 130 }}>
                <svg width="130" height="130" viewBox="0 0 130 130">
                  <circle cx="65" cy="65" r="58" fill="none" stroke={COLORS.rule} strokeWidth="9" />
                  <circle
                    cx="65" cy="65" r="58" fill="none" stroke={COLORS.brand} strokeWidth="9"
                    strokeLinecap="round" strokeDasharray={ringC}
                    strokeDashoffset={ringC * (1 - sampleScore / 100)}
                    transform="rotate(-90 65 65)"
                  />
                </svg>
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 34, fontWeight: 800, color: COLORS.ink, lineHeight: 1 }}>{sampleScore}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.ink4, marginTop: 2 }}>of 100</span>
                </div>
              </div>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 9 }}>
                {tierCats.map((c) => (
                  <div key={c.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: COLORS.ink3 }}>{c.label}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.ink4 }}>{c.pts}</span>
                    </div>
                    <div style={{ height: 5, backgroundColor: COLORS.ruleSoft, borderRadius: 999, overflow: 'hidden', display: 'flex' }}>
                      <div style={{ height: '100%', width: `${c.pct}%`, backgroundColor: c.color }} />
                      <div style={{ height: '100%', width: `${c.proof}%`, backgroundColor: `${c.color}59` }} />
                      <div style={{ height: '100%', width: `${c.verif}%`, backgroundColor: `${c.color}26` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, lineHeight: 1.5, color: COLORS.ink4 }}>
                What you share earns 60% of each category — uploading proof, then verification,
                unlocks the rest.
              </div>
            </div>
          </PreviewCard>

          {/* Program matches preview */}
          <PreviewCard label="Programs that fit your profile" icon={<Sparkles size={16} />}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
              {[
                { name: 'FHA', rate: '6.200%', blurb: 'Flexible credit requirements and just 3.5% down.', best: true },
                { name: 'Conventional 30-yr', rate: '6.400%', blurb: 'As little as 3% down; PMI drops off at 20% equity.', best: false },
                { name: 'VA Loan', rate: '6.000%', blurb: '0% down and no mortgage insurance — earned through service.', best: false },
              ].map((p) => (
                <div key={p.name} style={{
                  position: 'relative',
                  border: `1.5px solid ${p.best ? '#c7d2fe' : COLORS.rule}`,
                  borderRadius: 12, padding: '14px 16px',
                }}>
                  {p.best && (
                    <span style={{
                      position: 'absolute', top: -8, left: 12,
                      background: COLORS.brand, color: '#fff',
                      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em',
                      textTransform: 'uppercase', borderRadius: 999, padding: '2px 8px',
                    }}>
                      Best match
                    </span>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{p.name}</span>
                    <span style={{ fontSize: 16, fontWeight: 800, color: COLORS.ink }}>
                      {p.rate} <span style={{ fontSize: 10.5, fontWeight: 500, color: COLORS.ink4 }}>est.</span>
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.ink3, lineHeight: 1.5, marginTop: 3 }}>{p.blurb}</div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: COLORS.ink4, lineHeight: 1.5 }}>
                Sample rates for illustration — live pricing comes from competing lenders.
              </div>
            </div>
          </PreviewCard>

          {/* Lender matches preview */}
          <PreviewCard label="Lenders competing for you" icon={<FileText size={16} />}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
              <div style={{
                background: COLORS.brandSoft, border: `1px solid ${COLORS.brandBorder}`,
                borderRadius: 10, padding: '9px 12px',
              }}>
                <span style={{
                  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em',
                  textTransform: 'uppercase', color: COLORS.brand,
                }}>
                  You are Borrower #48213
                </span>
                <div style={{ fontSize: 11.5, color: COLORS.brandBody, lineHeight: 1.5, marginTop: 2 }}>
                  Lenders never see your name or contact info until you choose to reveal it.
                </div>
              </div>
              {[
                { name: 'Pacific Crest Home Loans', rate: '6.250%', status: 'Offer in hand', good: true },
                { name: 'Golden Gate Mutual', rate: '6.375%', status: 'Offer in hand', good: true },
                { name: 'Sierra Lending Co.', rate: '6.375%', status: 'Reviewing your file', good: false },
              ].map((l) => (
                <div key={l.name} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  border: `1px solid ${COLORS.rule}`, borderRadius: 12, padding: '12px 14px',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: COLORS.ink, marginTop: 2 }}>{l.rate}</div>
                  </div>
                  <span style={{
                    flex: 'none', fontSize: 10.5, fontWeight: 600, borderRadius: 999, padding: '4px 10px',
                    background: l.good ? COLORS.goodSoft : COLORS.warnSoft,
                    color: l.good ? COLORS.goodDeep : '#b45309',
                  }}>
                    {l.status}
                  </span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: COLORS.ink4, lineHeight: 1.5 }}>
                LoanKnow is free for you — lenders pay to compete for prepared borrowers.
              </div>
            </div>
          </PreviewCard>
        </div>

        <p style={{
          textAlign: 'center', marginTop: 36,
          fontSize: 13, color: COLORS.ink3, fontStyle: 'italic',
        }}>
          Above is a sample. Your actual score, programs, and offers are built from your specific profile.
        </p>
      </div>
    </section>
  );
};

const PreviewCard = ({ label, icon, children }) => (
  <div style={{
    padding: 22, borderRadius: 14,
    backgroundColor: '#fff',
    border: `1px solid ${COLORS.rule}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    display: 'flex', flexDirection: 'column',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 14, paddingBottom: 12,
      borderBottom: `1px solid ${COLORS.rule}`,
    }}>
      <div style={{ color: COLORS.brand }}>{icon}</div>
      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.6, color: COLORS.ink3,
      }}>
        {label}
      </div>
    </div>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Meet Owen
// ─────────────────────────────────────────────────────────────
const MeetOwen = () => (
  <section style={{
    padding: '96px 24px',
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #3730a3 100%)',
    color: 'white',
  }}>
    <div
      className="lk-meet-owen"
      style={{
        maxWidth: 1000, margin: '0 auto',
        display: 'grid', gridTemplateColumns: 'minmax(180px, 240px) 1fr',
        gap: 56, alignItems: 'center',
      }}
    >
      <div style={{
        width: 200, height: 200, borderRadius: '50%',
        backgroundColor: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 20px 60px rgba(99, 102, 241, 0.4)',
        margin: '0 auto',
      }}>
        <img src={OWEN_AVATAR} alt="Owen, LoanKnow's AI mortgage guide" style={{ width: 130, height: 130, objectFit: 'contain' }} />
      </div>
      <div>
        <SectionEyebrow color="#a5b4fc" center={false}>Meet Owen</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.6rem, 3vw, 2.25rem)',
          fontWeight: 800, lineHeight: 1.2,
          margin: '8px 0 18px', color: 'white',
        }}>
          The mortgage friend you wish you had on your last loan.
        </h2>
        <p style={{
          fontSize: 16, color: 'rgba(255,255,255,0.85)',
          lineHeight: 1.65, marginBottom: 24,
        }}>
          Owen is LoanKnow's AI mortgage guide — and he's upfront about being one. He walks you
          through every question in plain English, explains what lenders actually look for,
          answers anything you throw at him mid-conversation, and never views or retains your
          personal identifying information.
        </p>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          {[
            { icon: <Lightbulb size={16} />, label: 'Trained on real underwriting guidelines' },
            { icon: <Zap size={16} />, label: 'Available 24/7 with no wait times' },
            { icon: <Shield size={16} />, label: 'No commission. No bias. No agenda.' },
          ].map((f) => (
            <div
              key={f.label}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', borderRadius: 8,
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                fontSize: 13, color: 'rgba(255,255,255,0.95)',
              }}
            >
              <span style={{ color: '#a5b4fc' }}>{f.icon}</span>
              {f.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// Comparison table
// ─────────────────────────────────────────────────────────────
const Comparison = () => {
  const rows = [
    { trad: 'Hard credit pull on day one',                       us: 'No credit pull required to start' },
    { trad: 'You only see one lender\'s programs',              us: 'Owen matches you across the full program landscape' },
    { trad: 'You start by trusting the salesperson',            us: 'You start by knowing exactly where you stand' },
    { trad: 'You chase lenders and beg for callbacks',          us: 'Vetted lenders compete for your file — anonymously' },
    { trad: 'Your name and number get sold as a lead',          us: 'You\'re Borrower #48213 until you choose to reveal yourself' },
    { trad: 'Sales pressure from the first phone call',         us: 'No calls, no urgency, everything on your timeline' },
  ];

  return (
    <section style={{ padding: '96px 24px', backgroundColor: COLORS.bg }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <SectionEyebrow>Side by side</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 700, margin: '8px auto 56px',
        }}>
          The traditional way vs. the LoanKnow way.
        </h2>

        <div style={{
          backgroundColor: '#fff', borderRadius: 14,
          border: `1px solid ${COLORS.rule}`, overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            backgroundColor: COLORS.brandDark, color: 'white',
          }}>
            <div style={{ padding: '18px 24px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, opacity: 0.7 }}>
              Traditional path
            </div>
            <div style={{ padding: '18px 24px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#a5b4fc' }}>
              LoanKnow path
            </div>
          </div>
          {rows.map((r, i) => (
            <div
              key={i}
              style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                borderTop: i === 0 ? 'none' : `1px solid ${COLORS.rule}`,
              }}
            >
              <div style={{
                padding: '18px 24px', fontSize: 14, color: COLORS.ink3,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <X size={16} color={COLORS.bad} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{r.trad}</span>
              </div>
              <div style={{
                padding: '18px 24px', fontSize: 14, color: COLORS.ink,
                fontWeight: 500, backgroundColor: '#fcfcff',
                display: 'flex', alignItems: 'flex-start', gap: 10,
                borderLeft: `1px solid ${COLORS.rule}`,
              }}>
                <CheckCircle size={16} color={COLORS.good} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{r.us}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Honest live numbers (from Supabase)
// ─────────────────────────────────────────────────────────────
const LiveStats = ({ stats }) => {
  if (stats.isLoading || stats.applicationCount === 0) return null;
  return (
    <section style={{ padding: '64px 24px', backgroundColor: '#fff' }}>
      <div style={{
        maxWidth: 900, margin: '0 auto',
        padding: '40px 24px', borderRadius: 16,
        border: `1px solid ${COLORS.rule}`,
        background: 'linear-gradient(180deg, #ffffff 0%, #faf9f7 100%)',
      }}>
        <SectionEyebrow center>Live from LoanKnow</SectionEyebrow>
        <div style={{
          display: 'grid',
          gridTemplateColumns: stats.avgReadinessScore > 0 ? 'repeat(auto-fit, minmax(180px, 1fr))' : '1fr',
          gap: 32, marginTop: 24, textAlign: 'center',
        }}>
          <Stat
            value={stats.applicationCount.toLocaleString()}
            label="borrowers analyzed by Owen"
          />
          {stats.avgReadinessScore > 0 && (
            <Stat
              value={`${stats.avgReadinessScore}/100`}
              label="average Mortgage Readiness Score"
            />
          )}
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <div style={{
      fontSize: 'clamp(2.25rem, 4vw, 2.75rem)',
      fontWeight: 800, color: COLORS.ink, lineHeight: 1,
      letterSpacing: '-0.02em',
    }}>
      {value}
    </div>
    <div style={{ fontSize: 13, color: COLORS.ink3, marginTop: 8 }}>
      {label}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Real testimonials (only renders when data exists)
// ─────────────────────────────────────────────────────────────
const Testimonials = () => {
  if (!Array.isArray(testimonialsData) || testimonialsData.length === 0) return null;
  return (
    <section style={{ padding: '64px 24px', backgroundColor: COLORS.bg }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <SectionEyebrow>What borrowers say</SectionEyebrow>
        <div style={{
          display: 'grid', gap: 20, marginTop: 32,
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}>
          {testimonialsData.map((t, i) => (
            <div key={i} style={{
              padding: 24, borderRadius: 12, backgroundColor: '#fff',
              border: `1px solid ${COLORS.rule}`,
            }}>
              <p style={{ fontSize: 15, color: COLORS.ink, lineHeight: 1.6, fontStyle: 'italic', margin: '0 0 14px' }}>
                "{t.quote}"
              </p>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}>{t.name}</div>
              {t.location && <div style={{ fontSize: 12, color: COLORS.ink3 }}>{t.location}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────
const FAQ = () => {
  const items = [
    {
      q: 'Is this really free?',
      a: "Yes — completely. LoanKnow doesn't charge borrowers. We're paid by lenders only when you choose to share your file with one of them, and even then it never affects your rate or terms.",
    },
    {
      q: 'Do you do a credit pull?',
      a: "No. To get your Readiness Score and program matches, you simply state your credit range. A formal credit pull only happens later, with the lender you choose, on your timeline.",
    },
    {
      q: 'What happens to my information?',
      a: 'Your information is encrypted and never sold. Lenders see your file anonymously — as a borrower number, never your name — until you accept an offer. And Owen, our AI guide, never views or retains your personal identifying information.',
    },
    {
      q: 'Do I need to create a password?',
      a: 'No — your email is your whole account. When you want to save your progress or come back later, we email you a secure six-digit code. We never market, sell, or email anything that didn\'t come from your direction.',
    },
    {
      q: 'How accurate is the Readiness Score?',
      a: 'What you share in the chat earns up to 60% of each scoring category. Uploading proof documents, and then our verification that they match, unlocks the remaining 40% — so the score gets more accurate (and more trusted by lenders) as you go.',
    },
    {
      q: 'What if I already started with a lender?',
      a: "You can still use LoanKnow to validate where you stand independently. Many borrowers run a parallel readiness check to make sure they're getting a fair deal before they commit.",
    },
  ];

  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section style={{ padding: '96px 24px', backgroundColor: '#fff' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <SectionEyebrow>Common questions</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, marginBottom: 40, color: COLORS.ink,
          textAlign: 'center', maxWidth: 600, margin: '8px auto 40px',
        }}>
          Things people ask before they start.
        </h2>

        <div style={{
          backgroundColor: '#fff', borderRadius: 12,
          border: `1px solid ${COLORS.rule}`, overflow: 'hidden',
        }}>
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${COLORS.rule}` }}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 16,
                    padding: '20px 24px', background: 'none', border: 'none',
                    fontSize: 16, fontWeight: 600, color: COLORS.ink,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span>{item.q}</span>
                  {open ? <ChevronUp size={18} color={COLORS.ink3} /> : <ChevronDown size={18} color={COLORS.ink3} />}
                </button>
                {open && (
                  <div style={{
                    padding: '0 24px 20px', fontSize: 15,
                    color: COLORS.ink2, lineHeight: 1.65,
                  }}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Final CTA
// ─────────────────────────────────────────────────────────────
const FinalCTA = ({ startApplication }) => (
  <section style={{ padding: '64px 24px 96px', backgroundColor: COLORS.bg }}>
    <div style={{
      maxWidth: 880, margin: '0 auto',
      padding: '64px 40px', borderRadius: 20,
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 55%, #4338ca 100%)',
      color: 'white',
      textAlign: 'center', position: 'relative', overflow: 'hidden',
    }}>
      <div
        aria-hidden
        style={{
          position: 'absolute', top: -120, right: -120,
          width: 320, height: 320, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.4) 0%, transparent 70%)',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute', bottom: -100, left: -100,
          width: 280, height: 280, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(147,197,253,0.3) 0%, transparent 70%)',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.2, marginBottom: 16,
        }}>
          Stop guessing. Start knowing.
        </h2>
        <p style={{
          fontSize: 17, opacity: 0.85, lineHeight: 1.55,
          maxWidth: 540, margin: '0 auto 36px',
        }}>
          Chat with Owen, see your Readiness Score build live, and find the loan programs that
          fit you. No credit pull. No commitment. About 3 minutes.
        </p>
        <button
          onClick={() => startApplication('final_cta')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            backgroundColor: 'white', color: COLORS.brandDark,
            padding: '17px 40px', fontSize: 16, fontWeight: 700,
            borderRadius: 12, border: 'none', cursor: 'pointer',
            boxShadow: '0 8px 28px rgba(255,255,255,0.18)',
          }}
        >
          Get My Readiness Score — Free <ArrowRight size={18} />
        </button>
        <div style={{
          display: 'flex', gap: 24, justifyContent: 'center',
          flexWrap: 'wrap', marginTop: 24, fontSize: 13, opacity: 0.6,
        }}>
          <TrustItem>~3 minutes</TrustItem>
          <TrustItem>No credit check</TrustItem>
          <TrustItem>No sales calls</TrustItem>
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const SectionEyebrow = ({ children, color, center = true }) => (
  <div style={{
    fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 1.2,
    color: color || COLORS.brand,
    textAlign: center ? 'center' : 'left',
    marginBottom: 4,
  }}>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
const WelcomePage = () => {
  const { startApplication } = useEarlyAccess();

  const [liveStats, setLiveStats] = useState({
    applicationCount: 0,
    avgReadinessScore: 0,
    isLoading: true,
  });

  useEffect(() => {
    // Live stats read from `loan_applications`, which only exists once the
    // application backend is wired up. Skip the fetch + realtime subscription
    // entirely while we're in pre-launch (applicationLive=false) or when
    // Supabase isn't configured at all — otherwise every public page load
    // spams the console with "relation does not exist" errors.
    if (!isSupabaseConfigured || !LK_FEATURES.applicationLive) {
      setLiveStats((prev) => ({ ...prev, isLoading: false }));
      return undefined;
    }

    const fetchLiveStats = async () => {
      try {
        const { data: scoredApps, error } = await supabase
          .from('loan_applications')
          .select('readiness_score')
          .not('readiness_score', 'is', null);
        if (error) throw error;

        const scored = (scoredApps || []).filter((a) => a.readiness_score > 0);
        const avg = scored.length
          ? Math.round(scored.reduce((s, a) => s + a.readiness_score, 0) / scored.length)
          : 0;

        setLiveStats({
          applicationCount: scoredApps?.length || 0,
          avgReadinessScore: avg,
          isLoading: false,
        });
      } catch (err) {
        console.warn('Live stats unavailable:', err?.message);
        setLiveStats((prev) => ({ ...prev, isLoading: false }));
      }
    };
    fetchLiveStats();

    const subscription = supabase
      .channel('loan_applications_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'loan_applications' }, fetchLiveStats)
      .subscribe();
    return () => subscription.unsubscribe();
  }, []);

  return (
    <div style={{ backgroundColor: '#fff', color: COLORS.ink }}>
      <style>{`
        @media (max-width: 900px) {
          .lk-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
        }
        @media (max-width: 768px) {
          .lk-step-row { grid-template-columns: 1fr !important; }
          .lk-meet-owen { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `}</style>

      <Hero startApplication={startApplication} />
      <ProblemSection />
      <HowItWorks startApplication={startApplication} />
      <WhatYouGet />
      <MeetOwen />
      <Comparison />
      <LiveStats stats={liveStats} />
      <Testimonials />
      <FAQ />
      <FinalCTA startApplication={startApplication} />

      {/* Partner outreach (lenders + realtors) lives in ComplianceFooter's pill. */}
      <div style={{ padding: '20px 16px', textAlign: 'center', color: COLORS.ink3, fontSize: 13 }}>
        <Shield size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        Bank-level encryption. Your information is never sold.
      </div>

      <ComplianceFooter />
    </div>
  );
};

export default WelcomePage;
